import CryptoJS from "./aes.js";
import { unref, getCurrentInstance, nextTick } from "vue";
import config from "@/config";
import appBridge from "@utils/appBridge";

/**
 * 同步单例
 * @param f
 * @returns {(function(*): (*))|*}
 */
export const getSingleSync = (f) => {
    let data = null;
    return (refresh) => {
        if (data && !refresh) return data;
        data = f();
        return data;
    }
}

export const getRefValue = (ref) => {
    return ref.value;
}

/**
 * 异步单例
 * @param f
 * @returns {(function(*): Promise<*>)|*}
 */
export const getSingleAsync = (f) => {
    let data = null;
    return async (refresh, params = {}) => {
        if (data && !refresh) return data;
        data = await f(params);
        return data;
    }
}

/**
 * 获取右上角胶囊按钮位置
 * @type {(function(*): *)|*}
 */
export const getMenuButtonInfo = getSingleSync(uni.getMenuButtonBoundingClientRect)

/**
 * 获取顶部导航内容高，返回px的值（不包含px）
 * @returns {number}
 */
export const getNavBarContentHeight = () => {
    // #ifdef MP-WEIXIN
    return getMenuButtonInfo().height + 2;
    // #endif

    // #ifdef H5
    return 48
    // #endif
}

/**
 * 获取系统信息
 * 当 refresh 是 false 时，首次调用该方法会调用原生方法获取实时数据并缓存，以后每次调用会直接取缓存结果。
 * 当 refresh 是 true 时，每次调用该方法都会实时调用原生方法获取实时数据。
 * @param {Boolean} refresh - 默认 false，是否需要强制刷新缓存
 * @returns {Object} res - 返回系统信息
 * @returns {string} res.statusBarHeight - 手机状态栏的高度
 */
export const getSystemInfo = getSingleSync(uni.getSystemInfoSync)

/**
 * 获取状态栏的高度，返回px的值（不包含px）
 * @param {boolean} refresh - 默认 false，是否需要强制刷新缓存
 * @returns {number}
 */
export const getStatusBarHeight = (refresh = false) => {
    // #ifdef MP-WEIXIN
    return getSystemInfo(refresh).statusBarHeight + 7;
    // #endif

    // #ifdef H5
    return 7
    // #endif
}

/**
 * 获取顶部导航栏高度，返回px的值（不包含px）
 * @param {boolean} refresh - 默认 false，是否需要强制刷新缓存
 * @returns {number}
 */
export const getNavHeight = (refresh) => {
    return getStatusBarHeight(refresh) + getNavBarContentHeight();
}

/**
 * 是否含有上个页面
 * @returns {boolean}
 */
export const hasPrePage = () => {
    const pages = getCurrentPages();
    return pages && pages.length >= 2;
}

/**
 * json 转 url 参数
 * @param {object} json - 需要转换成字符串的 json 对象
 * @param {array} [ignoreFields=[]] 需要从 json 对象中排出的属性
 * @returns {string}
 */
export const jsonToUrlParam = (json = {}, ignoreFields = []) => {
    return Object.keys(json)
                 .filter(key => ignoreFields.indexOf(key) === -1)
                 .map(key => key + '=' + json[key]).join('&');
}

/**
 * url 对象转 json 对象
 * @param {string} url
 * @returns {Object}
 */
export function urlParamToJson(url) {
    if (!url) {
        return {};
    }

    let json = {};
    url.substring(url.indexOf('?') + 1)
       .trim()
       .split('&')
       .forEach(item => json[item.split('=')[0]] = item.split('=')[1]);

    return json;
}

/**
 * 生成跳转全路径
 * @param {string} path - 页面路径
 * @param {string} params - 页面参数
 * @returns {string} - 拼接好的全路径
 */
export const formatRouteUrl = (path = '', params) => {
    if (path.indexOf('/') !== 0) {
        path = '/' + path;
    }
    return path + (params.length > 0 ? "?" : '') + params;
}

/**
 * 提示方法
 * @param {String} title 提示文字
 * @param{Object} options - 扩展配置
 * @returns {(function(): void)|*}
 */
export const showToast = (title, options = {}) => {
    let _options = {}
    if (options.custom) {
        _options.image = {
            // 失败
            error: '/static/error.png',
            // 成功
            success: '/static/success.png',
            // 取消
            cancel: '/static/cancel.png',
        }[options.custom]
    }
    uni.showToast({
        title,
        icon: 'none',
        mask: true,
        duration: 2000,
        ..._options,
        ...options
    })
    return () => {
        try {
            uni.hideToast();
        } catch (e) {

        }
    }
}

/**
 * 提示方法
 * @param {String} title 提示文字
 * @param{Object} options - 扩展配置
 * @returns {(function(): void)|*}
 */
export const showToastAsync = (title, options = {}) => {
    return new Promise(async resolve => {
        let _options = {}
        if (options.custom) {
            _options.image = {
                // 失败
                error: '/static/error.png',
                // 成功
                success: '/static/success.png',
                // 取消
                cancel: '/static/cancel.png',
            }[options.custom]
        }
        uni.showToast({
            title,
            icon: 'none',
            mask: true,
            duration: 2000,
            ..._options,
            ...options
        })
        await sleep(options.duration || 2000)
        resolve(() => {
            try {
                uni.hideToast();
            } catch (e) {

            }
        })
    })
}

/**
 * 防御性取值
 * @param {Object|Array} source - 源对象
 * @param {string} path - 目标数据路径
 * @param {*} defaultValue - 取值异常后的兜底数据
 * @returns {*}
 * @example
 * let data = {};
 * // 如下取值必定异常，但最后d的取值是null
 * let d = _get(data,'a.b.c.d',null);
 * // 如下 d1 最后的取值的字符串 x
 * let d1 = _get(data,'a.b.c.d','x');
 *
 * let data1 = [{},{a:1}];
 * // 如取不到对应位置的值，a的值得0，否则是1
 * let a = _get(data1,'1.a',0);
 */
export const _get = (source, path, defaultValue = undefined) => {
    try {
        const paths = path.replace(/\[(\d+)\]/g, '.$1').split('.')
        let result = source
        for (const p of paths) {
            result = Object(result)[p]
            if (result === undefined) {
                return defaultValue
            }
        }
        return result
    } catch (e) {

    }
}

/**
 * 取值，取不到默认值未 null
 * @param source - 目标源数据
 * @param path - 取值路径
 * @returns {*}
 * @example
 * let data = {};
 * // 如下取值必定异常，能取到即为正常值，否则 d 的值为 null
 * let d = _getNull(data,'a.b.c.d');
 */
export const _getNull = (source, path) => {
    return _get(source, path, null);
}

/**
 * 安全字符串转 json 函数
 * @param data
 * @param defaultValue
 * @returns {any|string}
 * @private
 */
export const _parse = (data, defaultValue = '') => {
    try {
        return JSON.parse(data)
    } catch (e) {
        return defaultValue
    }
}

/**
 * 生成length长度 随机字符串
 * @param length
 * @returns {string}
 */
export const getRandomString = (length) => {
    let result = '';
    let strChars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let allChars = '0123456789' + strChars;
    for (let i = length; i > 0; --i) {
        let _str = allChars;
        if (i === length) {
            _str = strChars;
        }
        result += _str[Math.floor(Math.random() * _str.length)];
    }
    return result;
}

/**
 * 获取16位随机字符串
 * @returns {string}
 */
export const getRandomId = () => {
    return getRandomString(16)
}

/**
 * 深拷贝
 * @param obj
 */
export function deepCopy(obj) {
    function deepClone(data) {
        if (!data || !(data instanceof Object)) {
            return data;
        }
        let constructor = data.constructor;
        let result = new constructor();
        for (let key in data) {
            if (data.hasOwnProperty(key) && typeof data[key] != "function") {
                result[key] = deepClone(data[key]);
            }
        }
        return result;
    }

    try {
        return JSON.parse(JSON.stringify(obj));
    } catch (e) {
        console.error('deepCopy错误---', obj, e);
        return obj;
    }
}

/**
 * 是否是object
 * @param {*} t
 * @returns boolean
 */
export function isObject(t) {
    return t && (typeof t === 'object');
}

/**
 * 是否是array
 * @param {*} data
 * @returns boolean
 */
export function isArray(data) {
    return Object.prototype.toString.call(data) === `[object Array]`;
}

/**
 * 是否是Undefined
 * @param {*} data
 * @returns boolean
 */
export function isUndefined(data) {
    return Object.prototype.toString.call(data) === `[object Undefined]`;
}

/**
 * 是否是function
 * @param {*} data
 * @returns boolean
 */
export function isFunction(data) {
    return Object.prototype.toString.call(data) === `[object Function]`;
}

/**
 * 判断两个值是否相等
 * @param obj1
 * @param obj2
 * @returns {boolean}
 */
export function eq(obj1, obj2) {
    // 判断两个变量是否为对象类型
    let isObj = (toString.call(obj1) === '[object Object]' && toString.call(obj2) === '[object Object]');
    if (!isObj) {
        return false;
    }


    // 判断两个对象的长度是否相等，不相等则直接返回 fase
    let obj1Keys = Object.keys(obj1);
    let obj2Keys = Object.keys(obj2);
    if (Object.keys(obj1).length !== Object.keys(obj2).length) {
        return false;
    }


    // 判断两个对象的每个属性值是否相等
    for (const key in obj1) {
        // 判断两个对象的键是否相等
        if (obj2.hasOwnProperty.call(obj2, key)) {
            let obj1Type = toString.call(obj1[key]);
            let obj2Type = toString.call(obj2[key]);
            // 如果值是对象，则递归
            if (obj1Type === '[object Object]' || obj2Type === '[object Object]') {
                if (!isEqual(obj1[key], obj2[key])) {
                    return false;
                }
            } else if (obj1[key] !== obj2[key]) {
                return false; // 如果不是对象，则判断值是否相等
            }
        } else {
            return false;
        }
    }
    return true; // 上面条件都通过，则返回 true
}

/**
 * 通过经纬度获取距离
 * @param lat1
 * @param lng1
 * @param lat2
 * @param lng2
 * @returns {string}
 */
export const getDistanceByCoordinate = (lat1, lng1, lat2, lng2) => {
    /* 经纬度计算距离函数 */
    const Rad = d => {
        //根据经纬度判断距离
        return d * Math.PI / 180.0;
    };
    const radLat1 = Rad(lat1);
    const radLat2 = Rad(lat2);
    const a = radLat1 - radLat2;
    const b = Rad(lng1) - Rad(lng2);
    let s = 2 * Math.asin(Math.sqrt(Math.pow(Math.sin(a / 2), 2) + Math.cos(radLat1) * Math.cos(radLat2) * Math.pow(Math.sin(b / 2), 2)));
    s = s * 6378.137;
    s = Math.round(s * 10000) / 10000;
    s = s.toFixed(1);
    return s;
};

/**
 * 校验中国大陆手机号
 * @param {number} number - 手机号
 * @returns {boolean}
 */
export const authPhoneNumber = (number) => {
    return ('' + number).length > 1
}

/**
 * 是否刘海，有底部横条
 * @returns {string}
 */
export const isIphoneX = getSingleSync(() => {
    // #ifdef MP-WEIXIN
    const sysInfo = getSystemInfo();
    const _isX = ['x', 'X', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23'].find(id => {
        return [`iPhone ${id}`, `iPhone${id}`].find(k => {
            return sysInfo.model.startsWith(k);
        })
    })
    return !!_isX
    // #endif

    // #ifdef H5
    // 判断机型
    const u = navigator.userAgent;
    const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Linux') > -1; // 安卓系统
    const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/); // iOS系统
    if (isAndroid) {
        return false
    } else {
        if (!isIOS) {
            return false
        }
    }
    return window.innerHeight >= 721
    // #endif
})();


/**
 * 睡眠函数
 * @param {number} duration - 毫秒
 * @returns {Promise<unknown>}
 */
export const sleep = (duration = 3000) => {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve()
        }, duration);
    })
}

/**
 * loading
 * @param {string} title - 文案
 * @param {Object} options - 非必传，详细配置
 * @returns {function}
 * @example
 * // 展示loading
 * const hideLoading = showLoading('加载中...');
 * // 隐藏loading
 * hideLoading()
 */
export const showLoading = (title = '加载中...', options = {}) => {
    const timer = setTimeout(() => {
        uni.showToast({
            title,
            icon: 'loading',
            mask: true,
            duration: 10000,
            ...options,
        })
    }, options.delay || 200)
    return () => {
        try {
            timer && clearTimeout(timer);
            uni.hideToast();
        } catch (e) {

        }
    }
}

/**
 * 校验手机号
 * @param number
 * @returns {boolean}
 */
export const authPhone = (number) => {
    // 中国大陆手机号码正则表达式，支持国际区号+86
    const regex = /^1[3-9]\d{9}$/;
    return regex.test(number);
}

/**
 * 非立即执行防抖函数
 * @param {Function} func
 * @param {number} delay
 * @returns
 */
export const debounce = (func, delay) => {
    let timeout
    return function () {
        const _this = this
        const args = [...arguments]
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        timeout = setTimeout(() => {
            func.apply(_this, args)
        }, delay)
    }
}

/**
 * 立即执行函数，并且锁定 delay s
 * @param func
 * @param delay
 * @returns {(function(): void)|*}
 */
export const immediateLock = (func, delay = 3000) => {
    let timeout
    return function () {
        if (timeout) return;
        const _this = this
        const args = [...arguments]
        func.apply(_this, args)
        timeout = setTimeout(() => {
            timeout = null;
        }, delay)
    }
}

/**
 * rpx转px
 * @param rpx
 * @returns {number}
 */
export const rpx2px = (rpx) => {
    const screenWidth = getSystemInfo().safeArea.width
    return (screenWidth * Number.parseInt(rpx)) / 750
}

/**
 * px转rpx
 * @param px
 * @returns {number}
 */
export const px2rpx = (px) => {
    const screenWidth = getSystemInfo().safeArea.width
    return (750 * Number.parseInt(px)) / screenWidth
}

/**
 * 串联promise，函数管道
 * @param args
 * @returns {function(*): *}
 */
export const pipe = (...args) => {
    return (x) => {
        return args.reduce((acc, cur) => {
            if (!(acc instanceof Promise)) {
                acc = Promise.resolve(acc);
            }
            const ret = acc.then(cur);
            return ret;
        }, x);
    };
};

/**
 * 获取底部安全高度（单位rpx）
 * @returns {number}
 * @example
 * // safeHeight = 68
 * const safeHeight = getBottomSafeHeight();
 */
export const getBottomSafeHeight = () => {
    return isIphoneX ? 68 : 0
}

/**
 * 隐藏tabbar
 */
export const hideTabBar = () => {
    try {
        uni.hideTabBar();
    } catch (e) {

    }
}

export const showModal = ({ title, content }) => {
    return new Promise(resolve => {
        uni.showModal({
            title,
            content,
            success: (e) => {
                resolve(e)
            },
            fail: () => {
                resolve(null)
            },
        })
    })
}

export const encrypt = (word) => {
    let srcs = CryptoJS.enc.Utf8.parse(word);
    let key = CryptoJS.enc.Hex.parse(config.httpSign);

    let encrypted = CryptoJS.AES.encrypt(srcs, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
    });

    //返回大写十六进制加密结果
    return CryptoJS.enc.Hex.stringify(encrypted.ciphertext);
}

export const decrypt = (word) => {
    let encryptedHexStr = CryptoJS.enc.Hex.parse(word);
    let srcs = CryptoJS.enc.Base64.stringify(encryptedHexStr);
    let key = CryptoJS.enc.Hex.parse(config.httpSign);

    let decrypt = CryptoJS.AES.decrypt(srcs, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
    });

    //返回大写十六进制解密结果
    let decryptedStr = decrypt.toString(CryptoJS.enc.Utf8);
    return decryptedStr.toString();
}

export const whileArray = (data, f) => {
    return new Promise(async resolve => {
        let i = 0;
        while (data.length - i > 0) {
            await f(data[i], i);
            i++
        }
        resolve();
    })
}

/**
 * 校验身份证
 * @param idCard
 * @returns {boolean}
 */
export const authIdCard = (idCard) => {
    // 18位身份证号码正则表达式
    const idCardRegex = /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|10|11|12)(0[1-9]|[12]\d|31)\d{3}([0-9Xx])$/;
    return idCardRegex.test(idCard);
}

export const updateArray = (data, index, value) => {
    let _index = unref(index);
    let _value = unref(value);
    let _data = [...deepCopy(unref(data))];
    let _item = _data[_index];
    if (_value) {
        _data.splice(_index, 1, {
            ..._item,
            ..._value
        });
    } else {
        _data.splice(_index, 1);
    }
    return _data;
}

export const updateArrayRef = (data, index, value) => {
    let _item = unref(data)[unref(index)];
    if (value) {
        data.value.splice(unref(index), 1, {
            ..._item,
            ...unref(value)
        });
    } else {
        data.value.splice(unref(index), 1);
    }
}

export const updateObjectRef = (data, value) => {
    data.value = {
        ...data.value,
        ...value
    }
}

/**
 * 获取页面或组件实例
 * @returns {*}
 */
export const getInstance = () => {
    return getCurrentInstance().ctx
}

/**
 * 解码
 * @param data
 * @returns {string}
 */
export const decodeUrl = (data) => {
    return decodeURIComponent(decodeURIComponent(data))
}

/**
 * 生成原生路由跳转参数
 * @param url
 * @param query
 * @returns {*}
 */
export const getNativeRouteQuery = ({ url, query = {} }) => {
    if (query.query) {
        query.query = JSON.stringify(query.query)
    }
    return { url: formatRouteUrl(url, jsonToUrlParam(query)) }
}

/**
 * 设置 h5 标题
 * @param title
 */
export const setH5Title = (title) => {
    nextTick(() => {
        uni.setNavigationBarTitle({
            title
        })
        try {
            appBridge.setTitle(title);
        } catch (e) {
        }
    })
}

/**
 * 设置剪切板
 * @param data
 */
export const setCopy = (data) => {
    uni.setClipboardData({
        data,
        success: function () {
            showToast('复制成功！')
        }
    });
}

/**
 * 加码
 * @param data
 * @param single
 * @returns {string}
 */
export const encodeUrl = (data, single = false) => {
    if (single) {
        return encodeURIComponent(data)
    }
    return encodeURIComponent(encodeURIComponent(data))
}

/**
 * 拨打电话
 * @param phoneNumber
 */
export const makePhoneCall = (phoneNumber) => {
    uni.makePhoneCall({
        phoneNumber
    })
}

/**
 * 异步锁
 * @param f
 * @returns {function({params?: {}, success?: null}): Promise<{$success: boolean}>}
 */
export const asyncLock = (f) => {
    let httpLock = false;

    return async ({ params = {}, success = null }) => {
        if (httpLock) return;
        httpLock = true;

        let res = { $success: false };
        try {
            res = await f(params);
            success && success(res);
        } catch (e) {
            console.error(e)
        }
        httpLock = false;
        return res;
    }
}

/**
 * 设置剪切板
 * @param data
 */
export const setClientCopy = (data) => {
    uni.setClipboardData({
        data: '' + data,
        success() {
            showToast('复制成功！')
        },
        fail(e) {
            console.error(e)
        }
    });
}

/**
 * 获取网站项目前缀
 * http...
 * @returns {`${string}/web/#`}
 */
export const getWebRootPath = () => {
    return `${location.origin}${location.pathname}?__t=${+new Date()}#`
}

export const scroll2Top = () => {
    nextTick(() => {
        uni.pageScrollTo({
            scrollTop: Math.random(),
            duration: 0,
        })
    })
}
